/**
 * FixInspectionModal Component
 * Modal for reviewing and confirming suggested fixes for failed inspections
 */

import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Radio,
	RadioGroup,
	FormControlLabel,
	IconButton,
} from '@mui/material';
import {
	Close,
	PersonOutline,
	WarningAmber,
	LocationOn,
	CheckCircleOutline,
} from '@mui/icons-material';
import './index.scss';

const FixInspectionModal = ({
	open,
	onClose,
	inspection,
	suggestedDriver,
	driverOptions,
	onApplyFix,
}) => {
	const { messages } = useIntl();
	const [selectedOption, setSelectedOption] = useState('suggested');
	const [selectedDriverId, setSelectedDriverId] = useState(null);

	// Set the selected driver ID when suggestedDriver becomes available or modal opens
	useEffect(() => {
		if (open && suggestedDriver?.id) {
			setSelectedDriverId(suggestedDriver.id);
			setSelectedOption('suggested');
		}
	}, [open, suggestedDriver]);

	const handleOptionChange = (event) => {
		const value = event.target.value;
		setSelectedOption(value);

		if (value === 'suggested' && suggestedDriver) {
			setSelectedDriverId(suggestedDriver.id);
		} else if (value !== 'suggested' && value !== 'other') {
			setSelectedDriverId(value);
		}
	};

	const handleApplyFix = () => {
		if (onApplyFix && selectedDriverId) {
			onApplyFix({
				inspectionId: inspection?.id,
				driverId: selectedDriverId,
			});
		}
		onClose();
	};

	const issueMessage = inspection?.failureReason || 'Unknown issue';

	return (
		<Dialog
			open={open}
			onClose={onClose}
			className="fix-inspection-modal"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle className="modal-title">
				<div className="title-content">
					<PersonOutline className="title-icon" />
					<span>{messages['evirPx_fix_inspection'] || 'Fix Inspection'}</span>
				</div>
				<IconButton onClick={onClose} className="close-button" size="small">
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent className="modal-content">
				<p className="modal-description">
					{messages['evirPx_fix_inspection_description'] || 'Review and confirm the suggested fix for this failed inspection.'}
				</p>

				<div className="issue-box">
					<WarningAmber className="issue-icon" />
					<div className="issue-content">
						<span className="issue-label">{messages['evirPx_issue'] || 'Issue'}:</span>
						<span className="issue-message">{issueMessage}</span>
					</div>
				</div>

				{suggestedDriver && (
					<div className="suggested-driver-box">
						<div className="suggested-header">
							<LocationOn className="location-icon" />
							<span className="suggested-label">
								{messages['evirPx_suggested_driver'] || 'Suggested Driver'}:
							</span>
							<span className="confidence-badge">
								{messages['evirPx_high_confidence'] || 'high confidence'}
							</span>
						</div>
						<p className="suggested-explanation">
							{messages['evirPx_suggested_driver_explanation'] ||
								`Based on GPS location ${suggestedDriver.locationId || ''}, this inspection was likely performed by driver ${suggestedDriver.name} who was in that area at ${suggestedDriver.time || ''}.`}
						</p>
						<div className="suggested-driver-name">
							{suggestedDriver.name}
						</div>
					</div>
				)}

				<div className="driver-selection">
					<p className="selection-prompt">
						{messages['evirPx_is_this_correct'] || 'Is this correct? If not, please choose the correct driver:'}
					</p>

					<RadioGroup value={selectedOption} onChange={handleOptionChange}>
						{suggestedDriver && (
							<FormControlLabel
								value="suggested"
								control={<Radio />}
								label={
									<div className="radio-label">
										<CheckCircleOutline className="check-icon" />
										<span>{messages['evirPx_use_suggested'] || 'Use suggested'}: <strong>{suggestedDriver.name}</strong></span>
									</div>
								}
								className="driver-option suggested-option"
							/>
						)}

						{driverOptions
							?.filter((driver) => !suggestedDriver || driver.id !== suggestedDriver.id)
							.map((driver) => (
								<FormControlLabel
									key={driver.id}
									value={driver.id.toString()}
									control={<Radio />}
									label={`${driver.name} (ID: ${driver.id})`}
									className="driver-option"
								/>
							))}

						<FormControlLabel
							value="other"
							control={<Radio />}
							label={messages['evirPx_select_different_driver'] || 'Select different driver...'}
							className="driver-option other-option"
						/>
					</RadioGroup>
				</div>
			</DialogContent>

			<DialogActions className="modal-actions">
				<Button onClick={onClose} className="cancel-button">
					{messages['evirPx_cancel'] || 'Cancel'}
				</Button>
				<Button
					onClick={handleApplyFix}
					variant="contained"
					className="apply-button"
					disabled={!selectedDriverId}
				>
					{messages['evirPx_apply_fix'] || 'Apply Fix'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default FixInspectionModal;
